// 📦 React & Hooks
import React, { Fragment, useEffect, useState } from 'react';

// 🚦 Routing
import { Link, withRouter } from 'react-router-dom';

// 🧩 Redux
import { connect } from 'react-redux';

// 🔢 Utilities
import Decimal from 'decimal.js';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// 🧠 Helpers & Constants
import Helpers from '@Apis/helpers';
import { QUANTITY, DEFAULT_CUR_ROUND_OFF } from '@Apis/constants';

// 🧾 UI Library - H3
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';

// 🧰 Ant Design Components
import { Table, Select, Input } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';

// 🎛️ Common & Module-Specific Components
import SelectTax from '@Components/Admin/Common/SelectTax';
import ProductFilterV2 from '@Components/Common/ProductFilterV2';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import HideValue from '../../../../Common/RestrictedAccess/HideValue';
import PRZSelect from '../../../../Common/UI/PRZSelect';
import BatchSelectorDN from './BatchSelectorDN';

// 🖌️ Styles
import './style.scss';


const { Option } = Select;

const DNLines = ({
  data, user, loading, selectedGrn, toggleBatch, updateData, allStock, toggleAllStock, isQuickView, getBatches, title, formSubmitted, handleDelete, MONEY, billFromState, selectedCurrencyName, billToState, isLineWiseDiscount, debitNoteWithoutLines, debitNoteWithReturnedQyt, handleProductChange, handleProductChangeValue, addNewRow, selectedGrnForDN, tenantDepartmentId, isAdhocDnLines, priceMasking, dnFormFromAPInvoice, isVendorOverseas,

}) => {
  const [isEditing, setIsEditing] = useState(true);

  useEffect(() => {
    if (debitNoteWithoutLines) setIsEditing(true);
  }, [debitNoteWithoutLines]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const renderProductTypeTag = (productType) => {
    switch (productType) {
      case 'BUNDLE':
        return <H3Text text="Bundle" className="table-subscript table-subscript-tag" />;
      case 'SERVICE':
        return <H3Text text="Service" className="table-subscript table-subscript-tag" />;
      default:
        return <H3Text text="Goods" className="table-subscript table-subscript-tag" />;
    }
  };

  const renderProductDetails = (record) => (
    <div className="dn-table__product-name">
      <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
        <H3Text text={`#${record?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
      </Link>
      <div>
        {`${record?.product_sku_info?.ref_product_code ? `${record?.product_sku_info?.ref_product_code} - ` : ''}${record?.product_sku_name}`.trim()}
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ marginTop: '2px' }}>
          {renderProductTypeTag(record?.product_sku_info?.product_type)}
        </div>
      </div>
    </div>
  );

  const handleProductNameChange = (event, record) => {
    const copyData = JSON.parse(JSON.stringify(data));
    const updatedData = copyData.map((obj) => {
      if (obj.key === record.key) {
        return { ...obj, product_sku_name: event.target.value };
      }
      return obj;
    });
    updateData(updatedData);
  };

  const renderEditableProductName = (record) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {isEditing ? (
        <Input
          value={record?.product_sku_name}
          onChange={(event) => handleProductNameChange(event, record)}
          style={{
            marginRight: 8,
            height: '30px',
            width: '100%',
            borderRadius: '2px',
            border: '1px solid rgba(68, 130, 218, 0.2)',
            padding: '10px 8px',
            fontSize: '13px',
          }}
        />
      ) : (
        <div style={{ marginRight: 8 }}>{record?.product_sku_name}</div>
      )}
      {isEditing ? (
        <SaveOutlined onClick={handleSave} style={{ cursor: 'pointer', color: 'green' }} />
      ) : (
        <EditOutlined onClick={handleEdit} style={{ cursor: 'pointer', color: 'blue' }} />
      )}
    </div>
  );

  const handleProductClear = (key) => {
    const copyData = JSON.parse(JSON.stringify(data));
    const updatedData = copyData.map((item) => {
      if (item.key === key) {
        return {
          key: uuidv4(),
          asset1: '',
          product_name: '',
          quantity: '',
          returned_quantity: '',
          offerPrice: '',
          unitPrice: '',
          lot: 0,
          taxId: '',
          discount: 0,
          child_taxes: [{
            tax_amount: 0,
            tax_type_name: '',
          }],
        };
      }
      return item;
    });
    updateData(updatedData);
  };

  const renderProductFilter = (record) => (
    <ProductFilterV2
      record={record}
      productTypes={['STORABLE', 'SERVICE', 'NON_STORABLE']}
      handleProductChangeValue={handleProductChangeValue}
      handleProductChange={handleProductChange}
      isPurchaseProduct
      required
      pendingFromVendor
      showError={formSubmitted && !record.product_sku_name}
      onClear={(key) => handleProductClear(key)}
      disabled={!!record?.product_sku_id}
      excludedProducts={data?.map((item) => item?.tenant_product_id) || []}
      selectedTenant={user?.tenant_info?.tenants_id}
      tenantDepartmentId={tenantDepartmentId}
      showClear
    />
  );

  const renderContent = (record) => {
    if (selectedGrn) {
      return renderProductDetails(record);
    }

    if (debitNoteWithoutLines) {
      return renderEditableProductName(record);
    }

    return renderProductFilter(record);
  };

  const handleRemarksChange = (event, record) => {
    const copyData = JSON.parse(JSON.stringify(data));
    const updatedData = copyData.map((item) => {
      if (item.key === record.key) {
        item.remarks = event.target.value;
      }
      return item;
    });
    updateData(updatedData);
  };

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const productListColumns = [
    {
      title: `${debitNoteWithoutLines ? 'REASON' : 'PRODUCT'}`,
      fixed: 'left',
      width: '200px',
      render: (text, record) => (
        <Fragment>
          {renderContent(record)}
          {record?.productCategoryInfo?.category_path?.length > 0 && (
            <ProductCategoryLabel
              categoryPath={record?.productCategoryInfo?.category_path}
              categoryName={record?.productCategoryInfo?.category_path?.at(
                -1
              )}
              containerStyle={{
                width: "fit-content",
              }}
            />
          )}
          <textarea
            value={record?.remarks}
            onChange={(e) => handleRemarksChange(e, record)}
            className="dn-table__remarks"
            placeholder="Add description here.."
          />
        </Fragment>
      )
      ,
    },
    {
      title: dnFormFromAPInvoice ? "AP Inv QTY" : 'GRN QTY',
      width: '100px',
      hidden: !selectedGrn,
      render: (text, record) => (
        <div>
          {record?.grnRecivedQty
            ? `${QUANTITY(record?.grnRecivedQty, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()} `
            : '0'}
        </div>
      ),
    },
    {
      title: 'SELECTED QTY',
      hidden: !selectedGrn,
      render: (text, record) => (
        <Fragment>
          {record?.product_sku_info?.product_type?.toUpperCase() === 'SERVICE' && '-'}
          {record?.product_sku_info?.product_type?.toUpperCase() !== 'SERVICE'
            && (
              <Fragment>
                {Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'consumed_qty')
                  ? `${QUANTITY(Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'consumed_qty'), record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`
                  : <span style={{ color: 'red', fontSize: '11px', whiteSpace: 'nowrap' }}>OUT OF STOCK</span>}
              </Fragment>
            )}
          {record?.product_sku_info?.product_type?.toUpperCase() !== 'SERVICE' && Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'consumed_qty') ? (
            <H3Text
              text={`In ${record?.product_batches?.filter((item) => item?.batch_in_use)?.length || '0'} Batches`}
              className="table-subscript"
            />
          ) : ''}
        </Fragment>
      ),
    },
    {

      title: 'RETURNED QTY',
      width: '100px',
      hidden: !selectedGrn,
      render: (text, record) => `${QUANTITY(record?.existingDnQuantity || '0', record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()} `,
    },
    {
      title: ' QUANTITY',
      width: '100px',
      hidden: debitNoteWithoutLines,
      render: (text, record) => {
        const returnQty = record?.existingDnQuantity;
        const qytValidation = Number(record?.quantity) > (Number(record.grnRecivedQty) - Number(returnQty));
        const isStorable = !['NON_STORABLE', 'SERVICE'].includes(record?.product_sku_info?.product_type);
        return (
          <div>
            <div className="dn-qty-input">
              <H3FormInput
                required={!debitNoteWithoutLines}
                value={record.quantity}
                type="number"
                containerClassName={`orgInputContainer ${(formSubmitted && Number(record.quantity) <= 0) ? 'dn-lines__error' : ''}`}
                labelClassName="orgFormLabel"
                inputClassName="orgFormInput"
                // onChange={(event) => {
                //   const copyData = JSON.parse(JSON.stringify(data));
                //   const discountValue = Number(record.discount) || 0;
                //   const taxableValue = record?.lineDiscountType === 'Percent'
                //     ? (event.target.value * record.unitPrice) * (1 - discountValue / 100)
                //     : Math.max(event.target.value * record.unitPrice - discountValue, 0);

                //   const batches = record?.product_batches;
                //   let remainingQty = parseFloat(event.target.value);
                //   for (let i = 0; i < batches?.length; i++) {
                //     batches[i].quantity = 0;
                //   }

                //   if (isAdhocDnLines) {
                //     for (let i = 0; i < batches?.length; i++) {
                //       if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                //         if (remainingQty > 0 && batches[i].available_qty >= 0 && parseFloat(batches[i].available_qty || '0') > Number(remainingQty || '0')) {
                //           batches[i].quantity = remainingQty;
                //           remainingQty = 0;
                //         } else if (remainingQty > 0 && batches[i].available_qty >= 0 && parseFloat(batches[i].available_qty || '0') <= Number(remainingQty || '0')) {
                //           batches[i].quantity = batches[i].available_qty;
                //           remainingQty -= batches[i].available_qty;
                //         } else {
                //           batches[i].quantity = 0;
                //           remainingQty -= batches[i].quantity;
                //         }
                //       }
                //     }
                //   } else {
                //     for (let i = 0; i < batches?.length; i++) {
                //       if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                //         if (remainingQty > 0 && parseFloat(batches[i].quantity || '0') < Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0))) {
                //           batches[i].quantity = Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0)) > remainingQty ? remainingQty : Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0));
                //           remainingQty -= batches[i].quantity;
                //         } else {
                //           batches[i].quantity = 0;
                //           remainingQty -= batches[i].quantity;
                //         }
                //       }
                //     }
                //   }

                //   const updatedData = copyData.map((obj) => {
                //     if (obj.key === record.key) {
                //       let batchQuantity;
                //       if (!debitNoteWithReturnedQyt) {
                //         batchQuantity = batches?.map((batch) => (batch.batch_in_use ? { ...batch, returned_quantity: batch.quantity } : batch));
                //       } else {
                //         batchQuantity = batches;
                //       }

                //       return {
                //         ...obj,
                //         quantity: event.target.value,
                //         returned_quantity: (!debitNoteWithReturnedQyt) ? event.target.value : obj?.returned_quantity,
                //         product_batches: batchQuantity,
                //         child_taxes: Helpers.computeTaxation(taxableValue, record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes,
                //       };
                //     }
                //     return obj;
                //   });
                //   updateData(updatedData);
                // }}
                onChange={(event) => {
                  const copyData = JSON.parse(JSON.stringify(data));
                  const discountValue = new Decimal(record.discount || 0);
                  const quantityInput = new Decimal(event.target.value || 0);
                  const unitPrice = new Decimal(record.unitPrice || 0);

                  const taxableValue = record?.lineDiscountType === 'Percent'
                    ? quantityInput.times(unitPrice).times(new Decimal(1).minus(discountValue.div(100)))
                    : Decimal.max(quantityInput.times(unitPrice).minus(discountValue), 0);

                  const batches = record?.product_batches;
                  let remainingQty = quantityInput;

                  for (let i = 0; i < batches?.length; i++) {
                    batches[i].quantity = 0;
                  }

                  if (isAdhocDnLines) {
                    for (let i = 0; i < batches?.length; i++) {
                      const availableQty = new Decimal(QUANTITY(batches[i]?.available_qty || 0, record?.uom_info?.precision));
                      if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                        if (remainingQty.gt(0) && availableQty.gt(remainingQty)) {
                          batches[i].quantity = remainingQty.toNumber();
                          remainingQty = new Decimal(0);
                        } else if (remainingQty.gt(0) && availableQty.lte(remainingQty)) {
                          batches[i].quantity = availableQty.toNumber();
                          remainingQty = remainingQty.minus(availableQty);
                        } else {
                          batches[i].quantity = 0;
                        }
                      }
                    }
                  } else {
                    for (let i = 0; i < batches?.length; i++) {
                      const consumedQty = new Decimal(batches[i]?.consumed_qty || 0);
                      const dnGenQty = new Decimal(QUANTITY(batches[i]?.dn_gen_qty || 0, record?.uom_info?.precision));
                      const maxQty = Decimal.max(consumedQty.minus(dnGenQty), 0);

                      if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                        if (remainingQty.gt(0) && new Decimal(batches[i]?.quantity || 0).lt(maxQty)) {
                          const allocateQty = Decimal.min(maxQty, remainingQty);
                          batches[i].quantity = allocateQty.toNumber();
                          remainingQty = remainingQty.minus(allocateQty);
                        } else {
                          batches[i].quantity = 0;
                        }
                      }
                    }
                  }

                  const updatedData = copyData.map((obj) => {
                    if (obj.key === record.key) {
                      let batchQuantity;
                      if (!debitNoteWithReturnedQyt) {
                        batchQuantity = batches?.map((batch) => (
                          batch.batch_in_use ? { ...batch, returned_quantity: batch.quantity } : batch
                        ));
                      } else {
                        batchQuantity = batches;
                      }

                      return {
                        ...obj,
                        quantity: quantityInput.toNumber(),
                        returned_quantity: (!debitNoteWithReturnedQyt) ? quantityInput.toNumber() : obj?.returned_quantity,
                        product_batches: batchQuantity,
                        child_taxes: Helpers.computeTaxation(taxableValue.toNumber(), record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes,
                      };
                    }
                    return obj;
                  });
                  updateData(updatedData);
                }}
              />
              <H3Text text={record?.uom_info?.uqc?.toProperCase()} className="dn-qty-input-uom" />
            </div>
            {record.grnRecivedQty && (
              <H3Text
                text={(
                  <div>
                    Full Quantity
                  </div>
                )}
                onClick={() => {
                  const copyData = JSON.parse(JSON.stringify(data));
                  const batches = record?.product_batches;
                  for (let i = 0; i < batches?.length; i++) {
                    if ((allStock ? !batches[i]?.is_rejected_batch : true)) {
                      batches[i].quantity = Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0));
                    }
                  }
                  const updatedData = copyData.map((obj) => {
                    if (obj.key === record.key) {
                      return {
                        ...obj,
                        quantity: Number(Helpers.getValueTotalInObject(batches?.filter((item) => (allStock ? !item?.is_rejected_batch : true)), 'consumed_qty') - obj.existingDnQuantity),
                        product_batches: batches,
                        child_taxes: Helpers.computeTaxation(((Number(Helpers.getValueTotalInObject(batches?.filter((item) => (allStock ? !item?.is_rejected_batch : true)), 'quantity') - obj.existingDnQuantity)) * record.unitPrice) * (record.discount ? Number(100 - record.discount) / 100 : 1), record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes,
                      };
                    }
                    return obj;
                  });
                  updateData(updatedData);
                }}
                className="create-dn__qty"
              />
            )}
            {selectedGrn && (qytValidation || (record?.quantity > Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use)), 'consumed_qty'))) ? (
              <H3Text
                text={`Quantity should be less than or equal to ${qytValidation ? Number(record.grnRecivedQty) - Number(returnQty) : Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use)), 'consumed_qty')} ${record?.uom_info?.uqc?.toProperCase()} `}
                className="input-error"
              />
            ) : ''}
            {isAdhocDnLines && !selectedGrn && isStorable && (record?.quantity > Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => item?.batch_in_use), 'available_qty')) ? (
              <H3Text
                text={`Quantity should be less than or equal to ${Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => item?.batch_in_use), 'available_qty')}`}
                className="input-error"
              />
            ) : ''}
            {isAdhocDnLines}
          </div>
        );
      },
    },
    {
      title: 'Quantity to be Returned',
      width: '150px',
      hidden: !debitNoteWithReturnedQyt,
      render: (text, record) => {
        const returnQty = record?.existingDnQuantity;
        const qytValidation = Number(record.returned_quantity) > (Number(record.grnRecivedQty) - Number(returnQty));
        return (
          <div>
            <div className="dn-qty-input">
              <H3FormInput
                required={!debitNoteWithoutLines}
                value={record.returned_quantity}
                type="number"
                containerClassName={`orgInputContainer ${(formSubmitted && ((Number(record.returned_quantity) > Number(record.quantity)) || (Number(record.returned_quantity) < 0))) ? 'dn-lines__error' : ''}`}
                labelClassName="orgFormLabel"
                inputClassName="orgFormInput"
                onChange={(event) => {
                  const copyData = JSON.parse(JSON.stringify(data));
                  const discountValue = Number(record.discount) || 0;
                  const taxableValue = record?.lineDiscountType === 'Percent'
                    ? (Number(record.quantity) * record.unitPrice) * (1 - discountValue / 100)
                    : Math.max(Number(record.quantity) * record.unitPrice - discountValue, 0);

                  const batches = record?.product_batches;
                  let remainingQty = parseFloat(event.target.value);
                  for (let i = 0; i < batches?.length; i++) {
                    batches[i].returned_quantity = 0;
                  }

                  if (isAdhocDnLines) {
                    for (let i = 0; i < batches?.length; i++) {
                      if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                        if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') > Number(remainingQty || '0')) {
                          batches[i].returned_quantity = remainingQty;
                          remainingQty = 0;
                        } else if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') <= Number(remainingQty || '0')) {
                          batches[i].returned_quantity = batches[i].available_qty;
                          remainingQty -= batches[i].available_qty;
                        } else {
                          batches[i].returned_quantity = 0;
                          remainingQty -= batches[i].returned_quantity;
                        }
                      }
                    }
                  } else {
                    for (let i = 0; i < batches?.length; i++) {
                      if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                        if (remainingQty > 0 && parseFloat(batches[i].returned_quantity || '0') < Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0))) {
                          batches[i].returned_quantity = Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0)) > remainingQty ? remainingQty : Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0));
                          remainingQty -= batches[i].returned_quantity;
                        } else {
                          batches[i].returned_quantity = 0;
                          remainingQty -= batches[i].returned_quantity;
                        }
                      }
                    }
                  }

                  const updatedData = copyData.map((obj) => {
                    if (obj.key === record.key) {
                      const batchQuantity = batches;
                      return {
                        ...obj,
                        returned_quantity: event.target.value,
                        product_batches: batchQuantity,
                        child_taxes: Helpers.computeTaxation(taxableValue, record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes,
                      };
                    }
                    return obj;
                  });
                  updateData(updatedData);
                }}
              />
              <div className="dn-qty-input-uom">{record?.uom_info?.uqc?.toProperCase()}</div>
            </div>
            {(Number(record?.returned_quantity) > Number(record?.quantity)) ? (
              <H3Text
                text={`Quantity should be less than or equal to ${record?.quantity} ${record?.uom_info?.uqc?.toProperCase()}`}
                className="input-error"
              />
            ) : ''}
          </div>
        );
      },
    },
    {
      title: 'UNIT PRICE',
      render: (text, record) => (
        <div style={{ width: '100px' }}>
          <H3FormInput
            value={record.unitPrice}
            type="number"
            containerClassName={`orgInputContainer ${(formSubmitted && Number(record.unitPrice) <= 0) ? 'create-dn__input' : ''}`}
            labelClassName="orgFormLabel"
            inputClassName="orgFormInput"
            onChange={(e) => {
              const copyData = JSON.parse(JSON.stringify(data));
              const discountValue = Number(record.discount) || 0;
              const taxableValue = record?.lineDiscountType === 'Percent'
                ? (record.quantity * e.target.value) * (1 - discountValue / 100)
                : Math.max(record.quantity * e.target.value - discountValue, 0);

              copyData.map((item) => {
                if (item.key === record.key) {
                  // eslint-disable-next-line no-param-reassign
                  item.unitPrice = e.target.value;
                  item.child_taxes = Helpers.computeTaxation(taxableValue, record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes;
                }
                return data;
              });
              updateData(copyData);
            }}
            hideInput={isDataMaskingPolicyEnable && isHideCostPrice}
            popOverMessage={"You don't have access to view unit price"}
          />
        </div>
      ),
    },
    {
      title: 'TAX',
      render: (text, record) => (
        <div style={{ width: '100px' }}>
          <div className="orgInputContainer">
            <SelectTax
              onChange={(value) => {
                const copyData = JSON.parse(JSON.stringify(data));
                const discountValue = Number(record.discount) || 0;
                const taxableValue = record?.lineDiscountType === 'Percent'
                  ? (record.quantity * record.unitPrice) * (1 - discountValue / 100)
                  : Math.max(record.quantity * record.unitPrice - discountValue, 0);

                copyData.map((item) => {
                  if (item.key === record.key) {
                    item.taxId = value?.tax_id;
                    item.taxInfo = value;
                    item.child_taxes = Helpers.computeTaxation(taxableValue, value, billFromState, billToState)?.tax_info?.child_taxes;
                  }
                  return item;
                });
                updateData(copyData);
              }}
              errorOutline={formSubmitted && !record.taxId}
              selectedGST={record.taxId}
            />
          </div>
        </div>
      ),
      hidden: isVendorOverseas,
    },
    {
      title: 'DISCOUNT',
      render: (text, record) => {
        const lineDiscountAmount = record?.lineDiscountType === 'Percent' ? (((record.quantity * record.unitPrice) / 100) * record.discount) : record?.discount;
        return (
          <Fragment>
            {!isLineWiseDiscount ? (<div style={{ width: '110px', display: 'flex', alignItems: 'center' }}>
              <H3FormInput
                value={record.discount}
                type="number"
                containerClassName={`orgInputContainer ${(formSubmitted && Number(record.discount) < 0) ? 'create-pr__input' : ''}`}
                labelClassName="orgFormLabel"
                inputClassName="orgFormInput"
                onChange={(e) => {
                  const copyData = JSON.parse(JSON.stringify(data));
                  const discountValue = Number(e.target.value) || 0;
                  const taxableValue = record?.lineDiscountType === 'Percent'
                    ? (record.quantity * record.unitPrice) * (1 - discountValue / 100)
                    : Math.max(record.quantity * record.unitPrice - discountValue, 0);

                  copyData.map((item) => {
                    if (item.key === record.key) {
                      item.discount = e.target.value;
                      item.child_taxes = Helpers.computeTaxation(taxableValue, record?.taxInfo, billFromState, billToState)?.tax_info?.child_taxes;
                    }
                    return item;
                  });
                  updateData(copyData);
                }}
                disabled={isLineWiseDiscount}
              />
              <div className="orgInputContainer discount-type">
                <PRZSelect
                  value={record?.lineDiscountType}
                  onChange={(value) => {
                    const copyData = JSON.parse(JSON.stringify(data));
                    copyData.map((item) => {
                      if (item.key === record.key) {
                        item.lineDiscountType = value;
                      }
                      return item;
                    });
                    updateData(copyData);
                  }}
                  style={{
                    width: '50px',
                  }}
                  disabled={isLineWiseDiscount}
                  placeholder=""
                >
                  <Option key="Amount" value="Amount">
                    {`${selectedCurrencyName?.currency_symbol} `}
                  </Option>
                  <Option key="Percent" value="Percent">
                    %
                  </Option>
                </PRZSelect>
              </div>
            </div>) : (
              <div>
                <H3Text
                  text={
                    record?.discount ? record?.lineDiscountType === 'Percent' ? `${record?.discount}%` :
                      MONEY((record?.discount)?.toFixed(DEFAULT_CUR_ROUND_OFF), selectedCurrencyName?.currency_code)
                      : record?.lineDiscountType === 'Percent' ? `0%` : MONEY(0, selectedCurrencyName?.currency_code)
                  }
                />
              </div>
            )}
            {(record.quantity > 0 && record.unitPrice > 0 && record.discount > 0) && (
              <H3Text
                text={(
                  <div>
                    {record?.lineDiscountType === 'Percent'
                      ? ((isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver /> : `${selectedCurrencyName?.currency_symbol || '₹'} ${(lineDiscountAmount / record?.quantity).toFixed(2)} per unit`)
                      : (`${selectedCurrencyName?.currency_symbol || '₹'} ${(lineDiscountAmount / record?.quantity).toFixed(2)} per unit (${((record?.discount / (record.quantity * record.unitPrice)) * 100).toFixed(2)}%)`)}
                  </div>
                )}
                className="table-subscript"
              />
            )}
          </Fragment>
        );
      },
    },
    {
      title: 'LINE TOTAL',
      render: (text, record) => {
        const discountValue = Number(record.discount) || 0;
        const taxableValue = record?.lineDiscountType === 'Percent'
          ? (record.quantity * record.unitPrice) * (1 - discountValue / 100)
          : Math.max(record.quantity * record.unitPrice - discountValue, 0);
        return (
          <div style={
            { minWidth: '60px' }
          }
          >
            <H3Text
              text={record.taxInfo
                ? MONEY(Helpers.computeTaxation(taxableValue, record.taxInfo, billFromState, billToState)?.taxed_value, selectedCurrencyName?.currency_code) : 0}
              hideText={isDataMaskingPolicyEnable && isHideCostPrice}
              popOverMessage={"You don't have access to view line total"}
            />
          </div>
        );
      },
    },
    {
      title: '',
      fixed: 'right',
      render: (text, record) => (
        <Fragment>
          {data.length !== record.key && (
            <div className="update-dn__delete-line-button" onClick={() => handleDelete(record.key)}>
              <CloseOutlined />
            </div>
          )}
        </Fragment>
      ),
      // width: '40px',
    },
  ].filter((item) => !item.hidden);

  function updateBatchValue(key, value, label, batchId) {

    const copyData = JSON.parse(JSON.stringify(data));
    const updatedData = copyData.map((obj) => {
      if (obj.key === key) {
        let productBatches = obj?.product_batches;
        productBatches = productBatches?.map((productBatch) => {
          if (productBatch.batch_id === batchId) {
            // Check if label is 'consumed_qty' and apply logic based on available_qty
            if (debitNoteWithReturnedQyt ? label === 'returned_quantity' : label === 'quantity') {
              return {
                ...productBatch,
                [label]: value > productBatch?.available_qty ? productBatch?.available_qty : value,
              };
            }
            // For other labels, simply update with the provided value
            return {
              ...productBatch,
              [label]: value,
            };
          }
          return productBatch;
        });

        let qty = productBatches.reduce((sum, batch) => {
          const value = debitNoteWithReturnedQyt
            ? new Decimal(batch.returned_quantity || 0)
            : new Decimal(batch.quantity || 0);
          return sum.plus(value);
        }, new Decimal(0));

        if (debitNoteWithReturnedQyt) {
          obj.returned_quantity = qty;
        } else {
          obj.quantity = qty;
        }

        return {
          ...obj,
          product_batches: productBatches,
        };
      }
      return obj;
    });

    updateData(updatedData);
  }

  function getDataSource(data) {
    return JSON.parse(JSON.stringify(data));
  }

  return (
    <Fragment>
      <Table
        title={title}
        bordered
        loading={loading}
        showHeader
        size="small"
        scroll={{ x: 'max-content' }}
        columns={!selectedGrn ? productListColumns?.filter((column) => column.title !== 'GRN QUANTITY') : productListColumns}
        dataSource={getDataSource(data)}
        pagination={false}
        expandable={{
          defaultExpandAllRows: true,
          rowExpandable: (record) => record?.product_batches?.length,
          expandedRowRender: (record) => (
            <BatchSelectorDN
              allStock={allStock}
              upDateAllStock={() => {
                const copyData = JSON.parse(JSON.stringify(data));
                const filteredData = copyData?.map((item) => {
                  if (record.key === item.key) {
                    for (let i = 0; i < item.product_batches?.length; i++) {
                      item.product_batches[i].quantity = 0;
                    }
                    item.quantity = 0;
                  }
                  return {
                    ...item,
                  };
                });
                updateData(filteredData);
                toggleAllStock();
              }}
              batchMethod={record?.batchConsumptionMethod}
              updateBatchConsumptionMethod={(value) => {
                const copyData = JSON.parse(JSON.stringify(data));
                const sortedData = copyData?.map((item) => {
                  if (record.key === item.key) {
                    item.batchConsumptionMethod = value;
                    item.product_batches = Helpers.batchSelectionMethod(value, getBatches(item.product_batches), record.quantity, allStock, 'DebitNote', record?.uom_info);
                  }
                  return {
                    ...item,
                  };
                });
                updateData(sortedData);
              }}
              batches={record?.product_batches}
              uomInfo={record?.uom_info}
              productUomInfo={record?.product_sku_info?.purchase_uom_info}
              expiryDays={record?.product_sku_info?.expiry_days}
              itemsRow={record}
              data={getDataSource(data)}
              updateData={updateData}
              onToggleBatch={(batch, adjustmentRow) => toggleBatch(batch, adjustmentRow)}
              debitNoteWithReturnedQyt={debitNoteWithReturnedQyt}
              updateBatchValue={(value, label, batchId) => updateBatchValue(record?.key, value, label, batchId)}
            />
          ),
        }}
      />
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, PurchaseOrderReducers, OfferReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  updateCustomPurchaseOrderLoading: PurchaseOrderReducers.updateCustomPurchaseOrderLoading,
  selectedPurchaseOrder: PurchaseOrderReducers.selectedPurchaseOrder,
  getOfferByTenantSkuLoading: OfferReducers.getOfferByTenantSkuLoading,
  priceMasking: UserReducers.priceMasking,
});

/**
 *
 * @return {{}}
 */
const mapDispatchToProps = () => ({

});

DNLines.propTypes = {
  handleDelete: PropTypes.func,
  updateData: PropTypes.func,
  data: PropTypes.any,
  isQuickView: PropTypes.bool,
  formSubmitted: PropTypes.bool,
  loading: PropTypes.any,
  selectedGrn: PropTypes.any,
  isLineWiseDiscount: PropTypes.any,
  title: PropTypes.any,
  toggleBatch: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DNLines));
